import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import api from '../../config/api';
import type { ApiResponse, LoginRequest, RegisterRequest, TokenResponse, User } from '../../types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  rights: string[];   // right names granted to the user's role
  loading: boolean;
  error: string | null;
}

function loadFromStorage(): Pick<AuthState, 'user' | 'token' | 'isAuthenticated' | 'rights'> {
  try {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const rightsStr = localStorage.getItem('rights');
    const user: User | null = userStr ? JSON.parse(userStr) : null;
    const rights: string[] = rightsStr ? JSON.parse(rightsStr) : [];
    return { token, user, isAuthenticated: !!token && !!user, rights };
  } catch {
    return { token: null, user: null, isAuthenticated: false, rights: [] };
  }
}

const initialState: AuthState = {
  ...loadFromStorage(),
  loading: false,
  error: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────────

export const fetchMyRights = createAsyncThunk<string[], void, { rejectValue: string }>(
  'auth/fetchMyRights',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<string[]>>('/auth/my-rights');
      if (!response.data.success || !response.data.data) return rejectWithValue('Failed to fetch rights');
      return response.data.data;
    } catch {
      return rejectWithValue('Failed to fetch rights');
    }
  }
);

export const login = createAsyncThunk<TokenResponse, LoginRequest, { rejectValue: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<TokenResponse>>('/auth/login', credentials);
      if (!response.data.success || !response.data.data) {
        return rejectWithValue(response.data.message || 'Login failed');
      }
      return response.data.data;
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: { message?: string; errors?: string[]; requiresVerification?: boolean; email?: string };
        };
      };
      const status = err.response?.status;
      // 403 from the API means the account exists but email is unverified.
      // Encode as a sentinel so LoginPage can render the resend-verification UI.
      if (status === 403 && err.response?.data?.requiresVerification && err.response.data.email) {
        return rejectWithValue(`VERIFY_EMAIL:${err.response.data.email}`);
      }
      // errors is a dict { "Field": ["msg"] } — extract the first message
      const errorsDict = err.response?.data?.errors as Record<string, string[]> | undefined;
      const firstError = errorsDict ? Object.values(errorsDict).flat()[0] : undefined;
      // Surface the real message for account-status errors (deactivated / pending approval)
      if (status === 400 && firstError &&
          (firstError.includes('deactivated') || firstError.includes('pending approval'))) {
        return rejectWithValue(firstError);
      }
      // For generic auth failures (401, 404, 400), show a safe message (no info leakage)
      if (status === 401 || status === 404 || status === 400) {
        return rejectWithValue('Invalid email or password. Please try again.');
      }
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Login failed. Please try again.';
      return rejectWithValue(message);
    }
  }
);

export type GoogleLoginResult =
  | { requiresRoleSelection: true; credential: string }
  | (TokenResponse & { requiresRoleSelection?: false });

export const loginWithGoogle = createAsyncThunk<
  GoogleLoginResult,
  { credential: string; role?: string },
  { rejectValue: string }
>(
  'auth/loginWithGoogle',
  async ({ credential, role }, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<GoogleLoginResult>>('/auth/google', { credential, role });
      if (!response.data.success || !response.data.data) {
        return rejectWithValue(response.data.message || 'Google sign-in failed');
      }
      return response.data.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } };
      };
      const errorsDict = err.response?.data?.errors;
      const firstError = errorsDict ? Object.values(errorsDict).flat()[0] : undefined;
      return rejectWithValue(
        firstError || err.response?.data?.message || 'Google sign-in failed. Please try again.'
      );
    }
  }
);

export const register = createAsyncThunk<TokenResponse, RegisterRequest, { rejectValue: string }>(
  'auth/register',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<TokenResponse>>('/auth/register', data);
      if (!response.data.success || !response.data.data) {
        return rejectWithValue(response.data.message || 'Registration failed');
      }
      return response.data.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; errors?: string[] } } };
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Registration failed. Please try again.';
      return rejectWithValue(message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.rights = [];
      state.error = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('rights');
    },
    clearError(state) {
      state.error = null;
    },
    updateUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
  },
  extraReducers: (builder) => {
    // fetchMyRights
    builder.addCase(fetchMyRights.fulfilled, (state, action) => {
      state.rights = action.payload;
      localStorage.setItem('rights', JSON.stringify(action.payload));
    });

    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
      });

    // Google login (shares the same success/failure handling as password login)
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload.requiresRoleSelection) return;
        const payload = action.payload as TokenResponse;
        state.token = payload.token;
        state.user = payload.user;
        state.isAuthenticated = true;
        localStorage.setItem('token', payload.token);
        localStorage.setItem('user', JSON.stringify(payload.user));
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Google sign-in failed';
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Registration failed';
      });
  },
});

export const { logout, clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;
